import LinkButton from "components/link_button";
import { toBigInt } from "libs/bigint_helpers";
import { getRandomColor } from "libs/colors";
import { handleGenericError } from "libs/error_handling";
import { V2, V3 } from "libs/mjs";
import createProgressCallback, { type ProgressCallback } from "libs/progress_callback";
import Toast from "libs/toast";
import sortBy from "lodash-es/sortBy";
import { call, put, takeEvery } from "typed-redux-saga";
import type { BoundingBoxMinMaxType } from "types/bounding_box";
import type { FillMode, OrthoView, Vector2, Vector3 } from "viewer/constants";
import Constants, { FillModeEnum, Unicode } from "viewer/constants";
import getSceneController from "viewer/controller/scene_controller_provider";
import { mayEditAnnotation } from "viewer/model/accessors/annotation_accessor";
import { getMagInfo } from "viewer/model/accessors/dataset_accessor";
import { getTransformedDatasetBoundingBox } from "viewer/model/accessors/dataset_layer_transformation_accessor";
import { getDisabledInfoForTools } from "viewer/model/accessors/disabled_tool_accessor";
import { getActiveMagIndexForLayer } from "viewer/model/accessors/flycam_accessor";
import { AnnotationTool, Toolkit } from "viewer/model/accessors/tool_accessor";
import { enforceActiveVolumeTracing } from "viewer/model/accessors/volumetracing_accessor";
import { dispatchGetNewIdAsync } from "viewer/model/actions/actions";
import { addUserBoundingBoxAction } from "viewer/model/actions/annotation_actions";
import {
  type FloodFillAction,
  finishAnnotationStrokeAction,
  updateSegmentAction,
} from "viewer/model/actions/volumetracing_actions";
import BoundingBox from "viewer/model/bucket_data_handling/bounding_box";
import Dimensions from "viewer/model/dimensions";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select } from "viewer/model/sagas/effect_generators";
import { createOperationContext } from "viewer/model/sagas/operation_context_saga";
import { requestBucketModificationInVolumeTracing } from "viewer/model/sagas/saga_helpers";
import { runFloodFill } from "viewer/model/volumetracing/integration/flood_fill_driver";
import { Model, Store } from "viewer/singletons";
import {
  getSomeTracing,
  getUserBoundingBoxesThatContainPosition,
} from "../../accessors/tracing_accessor";

const NO_FLOODFILL_BBOX_TOAST_KEY = "NO_FLOODFILL_BBOX";
const NO_SUCCESS_MSG_WHEN_WITHIN_MS = 500;

export function* floodFill(): Saga<void> {
  yield* takeEvery("FLOOD_FILL", handleFloodFill);
}

function getMaximumBoundingBoxSizeForFloodfill(
  fillMode: FillMode,
  finestSegmentationLayerMag: Vector3,
): Vector3 {
  const maximumBoundingBoxMag1 = Constants.FLOOD_FILL_EXTENTS[fillMode];
  // If the finest mag of the segmentation layer is not Mag(1), the maximum bounding box needs to be scaled accordingly
  const maximumBoundingBoxInFinestMag = V3.scale3(
    maximumBoundingBoxMag1,
    finestSegmentationLayerMag,
  );
  return maximumBoundingBoxInFinestMag;
}

function* getBoundingBoxForFloodFillWhenRestricted(
  position: Vector3,
  currentViewport: OrthoView,
  finestSegmentationLayerMag: Vector3,
) {
  const fillMode = yield* select((state) => state.userConfiguration.fillMode);
  const bboxes = yield* select((state) => getUserBoundingBoxesThatContainPosition(state, position));
  if (bboxes.length === 0) {
    return {
      failureReason:
        "No bounding box encloses the clicked position. Either disable the bounding box restriction or ensure a bounding box exists around the clicked position.",
    };
  }
  const smallestBbox = sortBy(bboxes, (bbox) => new BoundingBox(bbox.boundingBox).getVolume())[0];

  const maxBboxSize = yield* call(
    getMaximumBoundingBoxSizeForFloodfill,
    fillMode,
    finestSegmentationLayerMag,
  );
  const maxBboxVolume = Constants.FLOOD_FILL_MULTIPLIER_FOR_BBOX_RESTRICTION * V3.prod(maxBboxSize);
  const bboxObj = new BoundingBox(smallestBbox.boundingBox);

  const bboxVolume =
    fillMode === FillModeEnum._3D
      ? bboxObj.getVolume()
      : // Only consider the 2D projection of the bounding box onto the current viewport
        V2.prod(
          Dimensions.getIndices(currentViewport).map((idx) => bboxObj.getSize()[idx]) as Vector2,
        );
  if (bboxVolume > maxBboxVolume) {
    return {
      failureReason: `The bounding box that encloses the clicked position is too large, containing ${bboxVolume} voxels. Shrink its size so that it does not contain more than ${maxBboxVolume} voxels.`,
    };
  }
  return smallestBbox.boundingBox;
}

function* getBoundingBoxForFloodFillWhenUnrestricted(
  position: Vector3,
  currentViewport: OrthoView,
  finestSegmentationLayerMag: Vector3,
) {
  const fillMode = yield* select((state) => state.userConfiguration.fillMode);
  const maxBoundingBoxSize = yield* call(
    getMaximumBoundingBoxSizeForFloodfill,
    fillMode,
    finestSegmentationLayerMag,
  );
  const halfBoundingBoxSize = V3.scale(maxBoundingBoxSize, 0.5);
  const currentViewportBounding = {
    min: V3.sub(position, halfBoundingBoxSize),
    max: V3.add(position, halfBoundingBoxSize),
  };

  if (fillMode === FillModeEnum._2D) {
    // Only use current plane
    const thirdDimension = Dimensions.thirdDimensionForPlane(currentViewport);
    const numberOfSlices = finestSegmentationLayerMag[thirdDimension];
    currentViewportBounding.min[thirdDimension] = position[thirdDimension];
    currentViewportBounding.max[thirdDimension] = position[thirdDimension] + numberOfSlices;
  }

  const datasetBoundingBox = yield* select((state) =>
    getTransformedDatasetBoundingBox(
      state.dataset,
      state.datasetConfiguration.nativelyRenderedLayerName,
    ),
  );
  const { min: clippedMin, max: clippedMax } = new BoundingBox(
    currentViewportBounding,
  ).intersectedWith(datasetBoundingBox);
  return {
    min: clippedMin,
    max: clippedMax,
  };
}

function* getBoundingBoxForFloodFill(
  position: Vector3,
  currentViewport: OrthoView,
  finestSegmentationLayerMag: Vector3,
): Saga<BoundingBoxMinMaxType | { failureReason: string }> {
  const isRestrictedToBoundingBox = yield* select(
    (state) => state.userConfiguration.isFloodfillRestrictedToBoundingBox,
  );
  if (isRestrictedToBoundingBox) {
    return yield* call(
      getBoundingBoxForFloodFillWhenRestricted,
      position,
      currentViewport,
      finestSegmentationLayerMag,
    );
  } else {
    return yield* call(
      getBoundingBoxForFloodFillWhenUnrestricted,
      position,
      currentViewport,
      finestSegmentationLayerMag,
    );
  }
}

function* handleFloodFill(floodFillAction: FloodFillAction): Saga<void> {
  const allowUpdate = yield* select(mayEditAnnotation);
  const disabledInfosForTools = yield* select(getDisabledInfoForTools);

  if (!allowUpdate || disabledInfosForTools[AnnotationTool.FILL_CELL.id].isDisabled) {
    return;
  }

  const { positionInLayerSpace: positionFloat, planeId } = floodFillAction;
  const volumeTracing = yield* select(enforceActiveVolumeTracing);
  if (volumeTracing.hasEditableMapping) {
    const message = "Volume modification is not allowed when an editable mapping is active.";
    Toast.error(message);
    console.error(message);
    return;
  }
  const segmentationLayer = yield* call(
    [Model, Model.getSegmentationTracingLayer],
    volumeTracing.tracingId,
  );
  const { cube } = segmentationLayer;
  const seedPosition = Dimensions.roundCoordinate(positionFloat);
  const activeCellId = volumeTracing.activeCellId;
  const requestedZoomStep = yield* select((state) =>
    getActiveMagIndexForLayer(state, segmentationLayer.name),
  );
  const magInfo = yield* call(getMagInfo, segmentationLayer.mags);
  const labeledZoomStep = magInfo.getClosestExistingIndex(requestedZoomStep);
  const additionalCoordinates = yield* select((state) => state.flycam.additionalCoordinates);
  const oldSegmentIdAtSeed = toBigInt(
    cube.getDataValue(seedPosition, additionalCoordinates, null, labeledZoomStep),
  );

  if (activeCellId === oldSegmentIdAtSeed) {
    Toast.warning("The clicked voxel's id is already equal to the active segment id.");
    return;
  }

  const sceneController = getSceneController();
  const isSplitToolkit = yield* select(
    (state) => state.userConfiguration.activeToolkit === Toolkit.SPLIT_SEGMENTS,
  );
  const splitBoundaryMesh = isSplitToolkit ? sceneController.getSplitBoundaryMesh() : null;

  if (isSplitToolkit) {
    if (!splitBoundaryMesh) {
      Toast.warning(
        `No split boundary found. Ensure that the active tree has at least two nodes. If you want to execute a normal fill operation, please switch to another toolkit (currently, the "Split Segments" toolkit is active).`,
      );
      return;
    }
    if (planeId !== "PLANE_XY") {
      Toast.warning(
        `Within the "Split Segments" toolkit, the fill tool is only supported in the XY viewport. Please use the tool in the XY viewport or switch to another toolkit.`,
      );
      return;
    }
  }

  const ctx = yield* createOperationContext({
    id: "FLOODFILL",
    description: "Floodfill is being computed.",
    behaviorWhenDisallowed: "ignore",
  });
  if (ctx == null) {
    const operations = yield* select((state) => state.operationContext.activeOperations);
    console.warn(
      `Ignoring floodfill request because another operation is already running (${operations.map((op) => op.id).join(", ")}).`,
    );
    return;
  }
  yield* ctx.execute(function* () {
    // As the flood fill will be applied to the volume layer,
    // the potentially existing mapping should be locked to ensure a consistent state.
    const isModificationAllowed = yield* call(
      requestBucketModificationInVolumeTracing,
      volumeTracing,
    );
    if (!isModificationAllowed) {
      return;
    }
    const finestSegmentationLayerMag = magInfo.getFinestMag();
    const boundingBoxForFloodFill = yield* call(
      getBoundingBoxForFloodFill,
      seedPosition,
      planeId,
      finestSegmentationLayerMag,
    );
    if ("failureReason" in boundingBoxForFloodFill) {
      Toast.warning(boundingBoxForFloodFill.failureReason, {
        key: NO_FLOODFILL_BBOX_TOAST_KEY,
      });
      return;
    } else {
      Toast.close(NO_FLOODFILL_BBOX_TOAST_KEY);
    }
    const fillMode = yield* select((state) => state.userConfiguration.fillMode);

    const progressCallback = createProgressCallback({
      pauseDelay: 200,
      successMessageDelay: 2000,
    });
    yield* call(progressCallback, false, "Performing floodfill...");
    const startTimeOfFloodfill = performance.now();

    const labeledMag = magInfo.getMagByIndexOrThrow(labeledZoomStep);
    // Floored, unlike brush's toMagVoxel: a flood-fill seed is used for direct
    // array indexing (resolveFloodFill), so it must land exactly on a
    // source-mag voxel. A mag1 position is only guaranteed to divide evenly
    // by the mag factor when it happens to be mag-aligned (e.g. z=43 at
    // mag-factor 2 is not), so this floor is required, not just tidiness.
    const toSourceMagVoxel = (position: Vector3): Vector3 => [
      Math.floor(position[0] / labeledMag[0]),
      Math.floor(position[1] / labeledMag[1]),
      Math.floor(position[2] / labeledMag[2]),
    ];
    // The bounds' max is exclusive (isInBoundingBox tests `< max`), so it has
    // to round *up*: a source-mag voxel q covers mag1 [q*mag, (q+1)*mag), and
    // the last voxel overlapping an unaligned max would be cut off by
    // flooring. E.g. max=43 at mag-factor 2 must stay 22, not become 21.
    const toExclusiveSourceMagBound = (position: Vector3): Vector3 => [
      Math.ceil(position[0] / labeledMag[0]),
      Math.ceil(position[1] / labeledMag[1]),
      Math.ceil(position[2] / labeledMag[2]),
    ];
    const stats = yield* call(runFloodFill, {
      cube,
      denseMags: magInfo.getDenseMags(),
      magIndex: labeledZoomStep,
      segmentId: activeCellId,
      additionalCoordinates: additionalCoordinates ?? null,
      seed: toSourceMagVoxel(seedPosition),
      is3D: fillMode === FillModeEnum._3D,
      bounds: {
        min: toSourceMagVoxel(boundingBoxForFloodFill.min),
        max: toExclusiveSourceMagBound(boundingBoxForFloodFill.max),
      },
      splitBoundaryMesh,
    });
    yield* put(finishAnnotationStrokeAction(volumeTracing.tracingId));
    yield* put(
      updateSegmentAction(
        volumeTracing.activeCellId,
        {
          anchorPosition: seedPosition,
          additionalCoordinates: additionalCoordinates || undefined,
        },
        volumeTracing.tracingId,
      ),
    );
    yield* call(
      notifyUserAboutResult,
      stats.wasBoundingBoxExceeded,
      startTimeOfFloodfill,
      progressCallback,
      fillMode,
      // Only read by notifyUserAboutResult when wasBoundingBoxExceeded is
      // true, in which case coveredBoundingBox is never null (see
      // FloodFillResult's doc comment) — the fallback here is unreachable.
      stats.coveredBoundingBox ?? { min: [0, 0, 0], max: [0, 0, 0] },
      oldSegmentIdAtSeed,
      activeCellId,
      seedPosition,
    );
  });

  if (floodFillAction.callback != null) {
    floodFillAction.callback();
  }
}

function* notifyUserAboutResult(
  wasBoundingBoxExceeded: boolean,
  startTimeOfFloodfill: number,
  progressCallback: ProgressCallback,
  fillMode: FillMode,
  coveredBoundingBox: BoundingBoxMinMaxType,
  oldSegmentIdAtSeed: bigint,
  activeCellId: bigint,
  seedPosition: Vector3,
) {
  let showSuccessMsg = false;
  if (wasBoundingBoxExceeded) {
    const isRestrictedToBoundingBox = yield* select(
      (state) => state.userConfiguration.isFloodfillRestrictedToBoundingBox,
    );
    // Don't notify the user about early-terminated floodfills if the floodfill
    // was configured to be restricted, anyway. Also, don't create a new bounding
    // box in that case.
    if (!isRestrictedToBoundingBox) {
      // The bounding box is overkill for the 2D mode because in that case,
      // it's trivial to check the borders manually.
      const createNewBoundingBox = fillMode === FillModeEnum._3D;
      const warningDetails = createNewBoundingBox
        ? "A bounding box that represents the labeled volume was added so that you can check the borders manually."
        : "Please check the borders of the filled area manually and use the fill tool again if necessary.";

      // Pre-declare a variable for the hide function so that we can refer
      // to that var within the toast content. We don't want to use message.destroy
      // because this ignores the setTimeout within the progress callback utility.
      // Without this, hide functions for older toasts could still be triggered (due to
      // timeout) that act on new ones then.
      let hideBox: { hideFn: () => void } | undefined;
      hideBox = yield* call(
        progressCallback,
        true,
        <>
          Floodfill is done, but terminated because{" "}
          {isRestrictedToBoundingBox
            ? "the labeled volume touched the bounding box to which the floodfill was restricted"
            : "the labeled volume got too large"}
          .
          <br />
          {warningDetails} {Unicode.NonBreakingSpace}
          <LinkButton onClick={() => hideBox?.hideFn()}>Close</LinkButton>
        </>,
        {
          successMessageDelay: 10000,
        },
      );
      if (createNewBoundingBox) {
        try {
          const tracingStoringBBoxes = yield* select((state) => getSomeTracing(state.annotation));
          const id = yield* call(
            dispatchGetNewIdAsync,
            Store.dispatch,
            tracingStoringBBoxes.tracingId,
            "BoundingBox",
          );
          yield* put(
            addUserBoundingBoxAction(
              {
                boundingBox: coveredBoundingBox,
                name: `Limits of flood-fill (source_id=${oldSegmentIdAtSeed}, target_id=${activeCellId}, seed=${seedPosition.join(
                  ",",
                )}, timestamp=${Date.now()})`,
                color: getRandomColor(),
                isVisible: true,
              },
              undefined,
              id,
            ),
          );
        } catch (error) {
          handleGenericError(error as Error, "Could not create a bounding box for the flood-fill.");
        }
      }
    } else {
      showSuccessMsg = true;
    }
  } else {
    showSuccessMsg = true;
  }

  const floodfillDuration = performance.now() - startTimeOfFloodfill;
  const wasFloodfillQuick = floodfillDuration < NO_SUCCESS_MSG_WHEN_WITHIN_MS;

  if (showSuccessMsg) {
    const { hideFn } = yield* call(progressCallback, true, "Floodfill done.");
    if (wasFloodfillQuick) {
      hideFn();
    }
  }
}
